import type {Draft} from 'immer'
import {produce} from 'immer'

import React, {memo, useCallback, useEffect, useRef, useState} from 'react'

import {useJobId} from '../../../EditPipelinePage/EditPipelinePage.hooks'
import {
  getDraftJobs,
  isSuccessMessageVisible,
} from '../../../EditPipelinePage/EditPipelinePage.selectors'
import {pipelineDraft, suggestions} from '../../../EditPipelinePage/slices/EditPipelinePage.slices'
import {usePipelineId} from '../../../hooks/pipeline'
import type {Step} from '../../../types'
import {StepTypes} from '../../../types'
import useJobStepsStoredValue from '../SettingsSidebar/SidebarContent/JobSettingsSidebar/JobSteps/hooks/useJobStepsStoredValue'
import SuggestionMessage from '../SuggestionMessage/SuggestionMessage'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

const HIDE_TIMEOUT = 6000

type Props = {
  stepIndex: number
  message: string
  type: StepTypes
}
function SuggestionSuccessMessage({stepIndex, message, type}: Props) {
  const jobId = useJobId()!
  const id = usePipelineId()
  const stepId = `step-${jobId}-${stepIndex + 1}`

  const visible = useAppSelector(state => isSuccessMessageVisible(state, stepId, type))

  const dispatch = useAppDispatch()
  const stepList = useAppSelector(state => getDraftJobs(state, id)?.[jobId]?.steps)
  const {addStoredJobStep, getStoredJobStep} = useJobStepsStoredValue(stepList ?? [], jobId)

  const [keepVisible, setKeepVisible] = useState(false)
  const timer = useRef<null | number>(null)

  useEffect(() => {
    if (visible && !keepVisible) {
      timer.current = window.setTimeout(
        () => dispatch(suggestions.actions.hideSuccessMessage({id: stepId})),
        HIDE_TIMEOUT,
      )
    }
    if (keepVisible) {
      timer.current && clearTimeout(timer.current)
    }
    return () => {
      timer.current && clearTimeout(timer.current)
    }
  }, [dispatch, keepVisible, stepId, visible])

  const updateSteps = useCallback(
    (recipe: (draft: Draft<Step[]>) => void) => {
      const steps = produce(stepList ?? [], recipe)
      const storedStep = steps[stepIndex]

      addStoredJobStep(storedStep, stepIndex)

      dispatch(
        pipelineDraft.actions.setSteps({
          id,
          jobId,
          steps,
        }),
      )
    },
    [stepList, stepIndex, addStoredJobStep, dispatch, id, jobId],
  )

  const onAction = useCallback(() => {
    dispatch(suggestions.actions.hideSuccessMessage({id: stepId}))
  }, [dispatch, stepId])

  const onCancel = useCallback(() => {
    dispatch(suggestions.actions.hideSuccessMessage({id: stepId}))
    updateSteps((draft: Draft<Step[]>) => {
      const prevStep = getStoredJobStep(StepTypes.CLI, stepIndex)
      if (prevStep) {
        draft[stepIndex] = prevStep
      }
    })
  }, [dispatch, getStoredJobStep, stepId, stepIndex, updateSteps])

  const onMouseEnter = useCallback(() => {
    setKeepVisible(true)
  }, [])

  const onMouseLeave = useCallback(() => {
    setKeepVisible(false)
  }, [])

  return (
    <SuggestionMessage
      message={message}
      actionText="Got it"
      cancelText="Undo"
      visible={visible}
      onCancelClick={onCancel}
      onActionClick={onAction}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  )
}

export default memo<Props>(SuggestionSuccessMessage)
